import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/utils/api';
import { 
  Vote, 
  CheckCircle2, 
  Clock, 
  Users, 
  TrendingUp,
  Shield,
  FileCheck,
  AlertCircle,
  ChevronRight,
  ExternalLink,
  Loader2,
  Lock,
  Unlock,
  BarChart3,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '@/utils/currency';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Status badge styles
const statusStyles = {
  DRAFT: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Draft' },
  ACTIVE: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Active' },
  ENDED: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Ended' },
  PASSED: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Passed' },
  REJECTED: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rejected' },
  CANCELLED: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Cancelled' },
};

// Proposal type icons
const typeIcons = {
  PROPERTY_SALE: '🏷️',
  MAJOR_RENOVATION: '🔨',
  RENT_CHANGE: '💰',
  MANAGER_APPOINTMENT: '👤',
  PROFIT_DISTRIBUTION: '📊',
  EMERGENCY_CAPITAL: '🚨',
  OTHER: '📋',
};

export default function GovernanceVoting() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [votingStep, setVotingStep] = useState(1); // 1: Select, 2: OTP, 3: Confirm
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [refId, setRefId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [voteCertificate, setVoteCertificate] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (!storedUser.id) {
      navigate('/auth');
      return;
    }
    setUser(storedUser);
    fetchProposals(storedUser.id);
  }, [navigate]);

  const fetchProposals = async (investorId) => {
    try {
      setLoading(true);
      const res = await api.get(`/governance/proposals/investor/${investorId}`);
      setProposals(res.data.proposals || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleStartVote = (proposal) => {
    setSelectedProposal(proposal);
    setSelectedChoice(null);
    setVotingStep(1);
    setOtp('');
    setRefId('');
    setShowVoteModal(true);
  };

  const handleSendOTP = async () => {
    if (!aadhaarNumber || aadhaarNumber.length !== 12) {
      toast.error('Please enter a valid 12-digit Aadhaar number');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post('/governance/vote/initiate', {
        proposal_id: selectedProposal.proposalId,
        aadhaar_number: aadhaarNumber
      });
      
      setRefId(res.data.ref_id);
      setVotingStep(2);
      toast.success('OTP sent to your Aadhaar-linked mobile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitVote = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    try {
      setSubmitting(true);
      const res = await api.post(`/governance/vote/submit?voter_id=${user.id}`, {
        proposal_id: selectedProposal.proposalId,
        choice: selectedChoice,
        ref_id: refId,
        otp: otp
      });
      
      setVoteCertificate(res.data);
      setVotingStep(3);
      toast.success('Vote recorded successfully!');
      
      // Refresh proposals
      fetchProposals(user.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const getTimeRemaining = (endTime) => {
    const end = new Date(endTime);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white p-4 md:p-8">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-lg border border-blue-500/30">
            <Vote className="w-6 h-6 text-blue-400" />
          </div>
          <h1 className="text-2xl md:text-3xl font-light tracking-tight">Governance Voting</h1>
        </div>
        <p className="text-white/50 text-sm md:text-base">
          Vote on property decisions. Secured by Aadhaar eSign + Polygon Blockchain.
        </p>
        
        {/* Security badges */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-xs text-green-400">
            <Shield className="w-3.5 h-3.5" />
            Aadhaar eSign Protected
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full text-xs text-purple-400">
            <Lock className="w-3.5 h-3.5" />
            Polygon Blockchain
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full text-xs text-blue-400">
            <FileCheck className="w-3.5 h-3.5" />
            IT Act 2000 Compliant
          </div>
        </div>
      </motion.div>

      {/* Proposals List */}
      <div className="max-w-6xl mx-auto space-y-4">
        {proposals.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8 text-center"
          >
            <Vote className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <h3 className="text-lg font-medium mb-2">No Active Proposals</h3>
            <p className="text-white/50 text-sm">
              You don't have any active voting proposals for your investments.
            </p>
          </motion.div>
        ) : (
          proposals.map((proposal, index) => {
            const status = statusStyles[proposal.status] || statusStyles.DRAFT;
            const typeIcon = typeIcons[proposal.proposalType] || '📋';
            const turnout = proposal.totalEligibleOwnership > 0 
              ? (proposal.votedOwnership / proposal.totalEligibleOwnership * 100) 
              : 0;
            
            return (
              <motion.div
                key={proposal.proposalId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden hover:border-white/10 transition-colors"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{typeIcon}</span>
                      <div>
                        <h3 className="font-medium text-lg">{proposal.title}</h3>
                        <p className="text-white/40 text-sm">
                          {proposal.proposalType.replace(/_/g, ' ')} • Unit: {proposal.unitId}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-white/60 text-sm mb-4 line-clamp-2">
                    {proposal.description}
                  </p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-[#111] rounded-lg p-3">
                      <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                        <Users className="w-3.5 h-3.5" />
                        Turnout
                      </div>
                      <div className="text-lg font-medium">{turnout.toFixed(1)}%</div>
                      <div className="text-xs text-white/40">of {proposal.quorumRequired}% needed</div>
                    </div>
                    
                    <div className="bg-[#111] rounded-lg p-3">
                      <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                        <BarChart3 className="w-3.5 h-3.5" />
                        Votes
                      </div>
                      <div className="text-lg font-medium">{proposal.votesCount}</div>
                      <div className="text-xs text-white/40">of {proposal.eligibleVoters} eligible</div>
                    </div>
                    
                    <div className="bg-[#111] rounded-lg p-3">
                      <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Your Stake
                      </div>
                      <div className="text-lg font-medium">{proposal.userOwnership?.toFixed(2)}%</div>
                      <div className="text-xs text-white/40">voting power</div>
                    </div>
                    
                    <div className="bg-[#111] rounded-lg p-3">
                      <div className="flex items-center gap-2 text-white/40 text-xs mb-1">
                        <Clock className="w-3.5 h-3.5" />
                        Time
                      </div>
                      <div className="text-lg font-medium">{getTimeRemaining(proposal.endTime)}</div>
                      <div className="text-xs text-white/40">
                        ends {new Date(proposal.endTime).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-white/40 mb-1">
                      <span>Quorum Progress</span>
                      <span>{turnout.toFixed(1)}% / {proposal.quorumRequired}%</span>
                    </div>
                    <Progress 
                      value={Math.min(turnout / proposal.quorumRequired * 100, 100)} 
                      className="h-2 bg-white/10"
                    />
                  </div>

                  {/* Vote Options Preview */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {proposal.options?.map((option) => {
                      const optionResult = proposal.results?.[option] || { count: 0, ownership: 0 };
                      const percentage = proposal.votedOwnership > 0 
                        ? (optionResult.ownership / proposal.votedOwnership * 100) 
                        : 0;
                      
                      return (
                        <div 
                          key={option}
                          className={`px-3 py-2 rounded-lg text-sm ${
                            proposal.userVote === option 
                              ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400' 
                              : 'bg-[#111] text-white/60'
                          }`}
                        >
                          {option}: {percentage.toFixed(1)}%
                          {proposal.userVote === option && (
                            <CheckCircle2 className="w-3.5 h-3.5 inline ml-1.5" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Action Button */}
                  <div className="flex items-center justify-between">
                    {proposal.hasVoted ? (
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <CheckCircle2 className="w-4 h-4" />
                        You voted: <span className="font-medium">{proposal.userVote}</span>
                      </div>
                    ) : proposal.status === 'ACTIVE' ? (
                      <Button
                        onClick={() => handleStartVote(proposal)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`vote-btn-${proposal.proposalId}`}
                      >
                        <Vote className="w-4 h-4 mr-2" />
                        Cast Your Vote
                      </Button>
                    ) : (
                      <span className="text-white/40 text-sm">Voting {proposal.status.toLowerCase()}</span>
                    )}
                    
                    <button className="text-white/40 hover:text-white text-sm flex items-center gap-1">
                      View Details <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Vote Modal */}
      <Dialog open={showVoteModal} onOpenChange={setShowVoteModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Vote className="w-5 h-5 text-blue-400" />
              {votingStep === 3 ? 'Vote Recorded!' : 'Cast Your Vote'}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              {votingStep === 1 && 'Select your choice and verify with Aadhaar'}
              {votingStep === 2 && 'Enter the OTP sent to your Aadhaar-linked mobile'}
              {votingStep === 3 && 'Your vote is secured on the blockchain'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Step 1: Select Choice */}
            {votingStep === 1 && (
              <>
                <div className="bg-[#111] rounded-lg p-4 mb-4">
                  <h4 className="font-medium mb-1">{selectedProposal?.title}</h4>
                  <p className="text-white/50 text-sm">{selectedProposal?.description}</p>
                </div>

                <div className="space-y-2">
                  <Label>Select your vote</Label>
                  {selectedProposal?.options?.map((option) => (
                    <button
                      key={option}
                      onClick={() => setSelectedChoice(option)}
                      className={`w-full p-3 rounded-lg border text-left transition-all ${
                        selectedChoice === option
                          ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                          : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>

                <div className="space-y-2 mt-4">
                  <Label>Aadhaar Number (for eSign)</Label>
                  <Input
                    type="text"
                    maxLength={12}
                    value={aadhaarNumber}
                    onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 12-digit Aadhaar"
                    className="bg-[#111] border-white/10 text-white"
                  />
                  <p className="text-xs text-white/40">
                    OTP will be sent to your Aadhaar-linked mobile for legal verification
                  </p>
                </div>

                <Button
                  onClick={handleSendOTP}
                  disabled={!selectedChoice || aadhaarNumber.length !== 12 || submitting}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  Send OTP & Continue
                </Button>
              </>
            )}

            {/* Step 2: Enter OTP */}
            {votingStep === 2 && (
              <>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                  <p className="text-blue-400 text-sm">
                    Voting for: <span className="font-medium">{selectedChoice}</span>
                  </p>
                  <p className="text-white/50 text-xs mt-1">
                    Your voting power: {selectedProposal?.userOwnership?.toFixed(2)}%
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Enter OTP</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="6-digit OTP"
                    className="bg-[#111] border-white/10 text-white text-center text-2xl tracking-widest"
                  />
                  <p className="text-xs text-white/40 text-center">
                    Enter the OTP sent to your Aadhaar-linked mobile
                  </p>
                </div>

                <Button
                  onClick={handleSubmitVote}
                  disabled={otp.length !== 6 || submitting}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Confirm & Submit Vote
                </Button>

                <button
                  onClick={() => setVotingStep(1)}
                  className="w-full text-white/50 text-sm hover:text-white"
                >
                  ← Go back
                </button>
              </>
            )}

            {/* Step 3: Success */}
            {votingStep === 3 && voteCertificate && (
              <>
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">Vote Successfully Recorded!</h3>
                  <p className="text-white/50 text-sm">
                    Your vote is legally binding and secured on the blockchain.
                  </p>
                </div>

                <div className="bg-[#111] rounded-lg p-4 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Vote ID</span>
                    <span className="font-mono text-xs">{voteCertificate.voteId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Your Vote</span>
                    <span className="text-blue-400 font-medium">{selectedChoice}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Vote Hash</span>
                    <span className="font-mono text-xs">{voteCertificate.voteHash?.slice(0, 16)}...</span>
                  </div>
                  {voteCertificate.blockchain?.transaction_hash && (
                    <div className="flex justify-between items-center">
                      <span className="text-white/50">Blockchain TX</span>
                      <a
                        href={`https://amoy.polygonscan.com/tx/${voteCertificate.blockchain.transaction_hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 flex items-center gap-1 text-xs"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowVoteModal(false)}
                    className="flex-1 bg-white/10 hover:bg-white/20"
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
